import { 
  signInWithEmailAndPassword, 
  signInWithCustomToken,
  signOut, 
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  User
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { firebaseService } from './firebase.service.js';
import { authSessionService } from './session.service.js';
import { deviceSessionService, DeviceSessionData } from './device-session.service.js';

// Re-export DeviceSessionData for use in other components
export type { DeviceSessionData };
import { getUserByUid } from '@cmms/api';

// Get Firebase instances from the centralized service
const auth = firebaseService.getAuth();
const db = firebaseService.getFirestore();
const storage = firebaseService.getStorage();

// Set persistence to local storage (survives page refresh)
setPersistence(auth, browserLocalPersistence);

export interface AuthUser {
  uid: string;
  email: string;
  displayName?: string;
  role?: string;
  companyId?: string;
  created_at?: number;
}

export interface LoginResult {
  user: AuthUser;
  existingSession?: DeviceSessionData;
  requiresConfirmation: boolean;
}

export class AuthService {
  private currentUser: AuthUser | null = null;
  private authStateListeners: ((user: AuthUser | null) => void)[] = [];

  constructor() {
    // Listen for authentication state changes
    onAuthStateChanged(auth, async (user: User | null) => {
      if (user) {
        // User is signed in
        this.currentUser = {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || undefined,
          created_at: user.metadata?.creationTime ? new Date(user.metadata.creationTime).getTime() : undefined
        };
        
        // Try to get user role and company from Firestore
        try {
          const userResult = await getUserByUid(db, user.uid);
          if (userResult.data) {
            const userData = userResult.data as any;
            this.currentUser.role = userData.model?.role || 'User';
            this.currentUser.companyId = userData.model?.company_id;
            this.currentUser.displayName = userData.model?.name || user.displayName || user.email?.split('@')[0] || 'User';
            this.currentUser.created_at = userData.model?.created_at || undefined;
          } else {
            console.warn('Could not fetch user data from Firestore');
            this.currentUser.role = 'User';
            this.currentUser.displayName = user.displayName || user.email?.split('@')[0] || 'User';
          }
        } catch (error) {
          console.warn('Could not fetch user role:', error);
          this.currentUser.role = 'User';
          this.currentUser.displayName = user.displayName || user.email?.split('@')[0] || 'User';
        }
      } else {
        // User is signed out
        this.currentUser = null;
        // Clean up device session management
        await deviceSessionService.deleteCurrentSession();
      }

      // Notify all listeners
      this.authStateListeners.forEach(listener => listener(this.currentUser));
    });

    // Listen for session expiration events
    window.addEventListener('session-expired', () => {
      this.handleSessionExpired();
    });
  }

  // Sign in with email and password
  async signIn(email: string, password: string, rememberMe: boolean = false): Promise<LoginResult> {
    try {
      // First authenticate with Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Get user role and company from Firestore
      let role = 'User';
      let companyId = '';
      try {
        const userResult = await getUserByUid(db, user.uid);
        if (userResult.data) {
          const userData = userResult.data as any;
          role = userData.model?.role || 'User';
          companyId = userData.model?.company_id || '';
        }
      } catch (error) {
        console.warn('Could not fetch user data during sign in:', error);
      }

      const authUser: AuthUser = {
        uid: user.uid,
        email: user.email || '',
        displayName: user.displayName || undefined,
        role,
        companyId,
      };

      // Check for existing sessions on the same device type
      const existingSession = await deviceSessionService.checkExistingSession();

      if (existingSession) {
        // User has existing session on same device type
        return {
          user: authUser,
          existingSession,
          requiresConfirmation: true
        };
      } else {
        // No existing session, create new one
        const deviceInfo = deviceSessionService.getDeviceInfo();
        await deviceSessionService.createSession(deviceInfo, undefined, rememberMe);
        
        // Start auth session management
        authSessionService.startSession(rememberMe);
        
        return {
          user: authUser,
          requiresConfirmation: false
        };
      }
    } catch (error: any) {
      console.error('Sign in error:', error);
      throw new Error(error.message || 'Sign in failed');
    }
  }

  // Confirm login and replace existing session
  async confirmLoginAndReplaceSession(rememberMe: boolean = false): Promise<AuthUser> {
    if (!this.currentUser) {
      throw new Error('No current user found');
    }

    try {
      // Delete existing session and create new one
      await deviceSessionService.deleteCurrentSession();
      const deviceInfo = deviceSessionService.getDeviceInfo();
      await deviceSessionService.createSession(deviceInfo, undefined, rememberMe);
      
      // Start auth session management
      authSessionService.startSession(rememberMe);
      
      return this.currentUser;
    } catch (error: any) {
      console.error('Error confirming login:', error);
      throw new Error('Failed to confirm login');
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      // End auth session management
      authSessionService.endSession();
      await deviceSessionService.deleteCurrentSession();
      await signOut(auth);
    } catch (error: any) {
      console.error('Sign out error:', error);
      throw new Error(error.message || 'Sign out failed');
    }
  }

  // Sign out from all devices
  async signOutAllDevices(): Promise<void> {
    try {
      if (!this.currentUser) {
        throw new Error('No current user found');
      }

      // Delete all sessions for this user
      await deviceSessionService.deleteAllUserSessions();
      
      // End auth session management
      authSessionService.endSession();
      await signOut(auth);
    } catch (error: any) {
      console.error('Sign out all devices error:', error);
      throw new Error(error.message || 'Sign out failed');
    }
  }

  // Get all active sessions for current user
  async getUserSessions(): Promise<DeviceSessionData[]> {
    if (!this.currentUser) {
      return [];
    }

    try {
      return await deviceSessionService.getUserSessions();
    } catch (error: any) {
      console.error('Error getting user sessions:', error);
      return [];
    }
  }

  // Validate current session
  async validateSession(): Promise<boolean> {
    try {
      return await deviceSessionService.validateSession();
    } catch (error: any) {
      console.error('Error validating session:', error);
      return false;
    }
  }

  // Handle session expiration
  private async handleSessionExpired(): Promise<void> {
    this.currentUser = null;
    
    // Clear legacy session
    authSessionService.endSession();
    
    // Sign out from Firebase Auth
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out on session expiration:', error);
    }
    
    // Dispatch event for UI to handle
    window.dispatchEvent(new CustomEvent('auth-session-expired', {
      detail: { message: 'Your session has expired. Please log in again.' }
    }));
  }

  // Get current user
  getCurrentUser(): AuthUser | null {
    return this.currentUser;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  // Add auth state listener
  onAuthStateChanged(listener: (user: AuthUser | null) => void): () => void {
    this.authStateListeners.push(listener);
    
    // Call listener immediately with current state
    listener(this.currentUser);
    
    // Return unsubscribe function
    return () => {
      const index = this.authStateListeners.indexOf(listener);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }

  // Check if current user is admin
  isAdmin(): boolean {
    return this.currentUser?.role === 'admin';
  }

  // Check if current user is engineer
  isEngineer(): boolean {
    return this.currentUser?.role === 'engineer';
  }

  // Check if current user is super admin
  isSuperAdmin(): boolean {
    return this.currentUser?.role === 'super admin';
  }

  // Check if current user is shop floor
  isShopFloor(): boolean {
    return this.currentUser?.role === 'shop floor';
  }

  // Check if current user has specific role
  hasRole(role: string): boolean {
    return this.currentUser?.role === role;
  }

  // Check if user has permission level
  hasPermissionLevel(requiredLevel: number): boolean {
    const roleLevels = {
      'super admin': 4,
      'admin': 3,
      'engineer': 2,
      'shop floor': 1
    };
    const userLevel = roleLevels[this.currentUser?.role as keyof typeof roleLevels] || 0;
    return userLevel >= requiredLevel;
  }
}

// Create and export singleton instance
export const authService = new AuthService();

export { auth, db, storage, signOut }; 